import { KIZEN_PREFIX, LocalStorage, SessionStorage } from './Storage';

const KEEP_ALIVE_LAST_ACTIVITY_KEY = `${KIZEN_PREFIX}keep-alive-last-activity`;
const SESSION_LOGIN_KEY = `${KIZEN_PREFIX}login`;
const SESSION_OAUTH_KEY = `${KIZEN_PREFIX}oauth`;
const KIZEN_AUTHORIZED_KEY = `${KIZEN_PREFIX}auth`; // this key is used to sync browser instances of kizen !!
const KIZEN_SELECTED_BUSINESS_KEY = `${KIZEN_PREFIX}selected-business`; // this key is used to sync browser instances of kizen !!

const illegalPaths = [
  '/dashboard',
  '/choose-business',
  '/not-found',
  '/reset',
  '/inactive-business',
  '/permissions-needed',
  '/sso',
  '/create-new-business',
];
const redirectablePaths = ['/login'];

type AuthState = {
  authorized?: boolean;
  businessId?: string;
};

class AuthLocalStorage extends LocalStorage<AuthState> {
  constructor(
    key: string,
    { serialize = JSON.stringify, deserialize = JSON.parse } = {}
  ) {
    super(key, { serialize, deserialize });
  }

  set authorized(authorized: boolean) {
    const val = { ...this.val, authorized };
    this.val = val;
  }

  get authorized(): boolean {
    const { authorized } = this.val || {};
    return authorized ?? false;
  }

  set businessId(businessId: string) {
    const val = { ...this.val, businessId };
    this.val = val;
  }

  get businessId(): string | undefined {
    const { businessId } = this.val || {};
    return businessId;
  }
}

class RedirectLocalStorage extends LocalStorage {
  constructor(
    key: string,
    { serialize = JSON.stringify, deserialize = JSON.parse } = {}
  ) {
    super(key, { serialize, deserialize });
  }

  get pathOriginal() {
    return this.val;
  }

  get path(): string | null {
    return this.sanitizePath(this.val);
  }

  get pathSanitized() {
    return this.fullSanitizePath(this.val);
  }

  set path(path: string) {
    this.val = path;
  }

  sanitizePath(path: string) {
    return illegalPaths.some((illegal) => String(path).startsWith(illegal))
      ? null
      : path;
  }

  fullSanitizePath(path: string) {
    return [...illegalPaths, ...redirectablePaths].some((illegal) =>
      String(path).startsWith(illegal)
    )
      ? null
      : path;
  }
}

export const authLocalStorage = new AuthLocalStorage(KIZEN_AUTHORIZED_KEY);

export const keepAliveLastActivitySessionStorage = new SessionStorage(
  KEEP_ALIVE_LAST_ACTIVITY_KEY
);

export const loginSessionStorage = new SessionStorage(SESSION_LOGIN_KEY);

export const oauthSessionStorage = new SessionStorage(SESSION_OAUTH_KEY);

export const redirectLocalStorage = new RedirectLocalStorage('loginRedirect');

// the oauth stash may hold an absolute url on the api host, which react-router
// would treat as a path and append to the current origin
export const isExternalUrl = (url: string | null | undefined): boolean =>
  Boolean(url) && /^https?:\/\//i.test(url as string);

export const selectedBusinessLocalStorage = new LocalStorage(
  KIZEN_SELECTED_BUSINESS_KEY
);
